"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.documentation = exports.signature = exports.name = void 0;
exports.implementation = implementation;
const date_fns_tz_1 = require("date-fns-tz");
const error_1 = require("../error");
const constants_1 = require("./constants");
exports.name = "currentDateTime";
exports.signature = "<ss?:s>";
function implementation(formatStr, timezone) {
    try {
        if (typeof formatStr !== "string") {
            throw new Error(`parameter format expected type string, received type ${typeof formatStr}`);
        }
        if (timezone && typeof timezone !== "string") {
            throw new Error(`parameter timezone expected type string, received type ${typeof timezone}`);
        }
        const result = (0, date_fns_tz_1.formatInTimeZone)(new Date(), timezone !== null && timezone !== void 0 ? timezone : "UTC", formatStr);
        return result;
    }
    catch (e) {
        const error = e;
        if (error instanceof RangeError) {
            // This is the only link that is thrown as a part of error's message by date-fns's functions we're using
            // https://github.com/date-fns/date-fns/search?q=https%3A%2F%2Fgit.io%2F
            error.message = error.message.replace("; see: https://git.io/fxCyr", "");
        }
        /**
         * We are adding a `code` to mock a native JsonataError, but using our own `code` value
         * to differentiate from the errors defined by Jsonata itself.
         *
         * See existing codes here: https://github.com/jsonata-js/jsonata/blob/65e854d6bfee1d1413ebff7f1a185834c6c42265/src/jsonata.js#L1933
         */
        const stediErrorWithJsonataCode = error;
        const stediErrorMessagePrefix = error_1.CUSTOM_STEDI_JSONATA_ERROR_MESSAGES[error_1.CustomStediJsonataError.STEDI002];
        const functionArguments = [`format=\`${formatStr}\``];
        if (timezone) {
            functionArguments.push(`timezone=\`${timezone}\``);
        }
        const functionArgumentsString = functionArguments.join(", ");
        stediErrorWithJsonataCode.code = error_1.CustomStediJsonataError.STEDI002;
        stediErrorWithJsonataCode.message = `${stediErrorMessagePrefix}: ${error.message}, with arguments: ${functionArgumentsString}`;
        throw stediErrorWithJsonataCode;
    }
}
exports.documentation = {
    label: "$currentDateTime",
    detail: "$currentDateTime(format [, timezone])",
    documentation: `Returns a current date and time formatted string according to \`format\`. Format must be specified [using unicode tokens](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
Optionally, a timezone can be specified. If not specified, the timezone is assumed to be UTC.
${constants_1.STEDI_FUNCTION_DISCLAIMER}

Note there are 4 tokens that might cause confusion:
- \`D\` and \`DD\` that represent the day of a year (1, 2, ..., 365, 366) are often confused with \`d\` and \`dd\` that represent the day of a month (1, 2, ..., 31).
- \`y\` and \`yyyy\` that represent the local week-numbering year (44, 01, 00, 17) are often confused with \`yy\` and \`yyyy\` that represent the calendar year.

On top of the \`$currentDateTime\` function, we also provide popular date/time formats as constants under \`$dateTime\` object:

| Variable                | Value                          |
|-------------------------|--------------------------------|
| $dateTime.RFC3339       | "yyyy-MM-dd'T'HH:mm:ssXXX"     |
| $dateTime.RFC3339Millis | "yyyy-MM-dd'T'HH:mm:ss.SSSXXX" |
| $dateTime.EDIDate       | "yyMMdd"                       |
| $dateTime.EDIDateLong   | "yyyyMMdd"                     |

**Example**

| Function call                             | Returned value             |
| ----------------------------------------- | ---------------------------|
| $currentDateTime("yyyy-MM-dd")            | "2022-02-09"               |
| $currentDateTime($dateTime.EDIDate)       | "220209"                   |
| $currentDateTime($dateTime.RFC3339Millis) | "2022-02-09T08:23:52.000Z" |
| $currentDateTime("yyyy-MM-dd", "EST")     | "2022-02-09"               |`,
    insertText: "\\$currentDateTime($1)",
    parameters: [
        {
            label: "format",
            documentation: `Date format of the input string using [using unicode tokens](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table)`,
        },
        {
            label: "timezone",
            documentation: `Optional, specifies the timezone in which the date/time is generated. Defaults to UTC.`,
        },
    ],
};
